import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { BrandVoiceTextarea } from './BrandVoiceFields'
import {
  isDefaultBrandVoiceText,
  joinBrandVoiceFilenames,
} from './brandVoiceText'

const t = buildTranslate('recovery_ai.brand_voice')

// Vertical gap between the textarea and the row beneath it (CLAUDE.md "extract
// magic numbers").
const ROW_MARGIN_TOP = '12px'

type Props = {
  value: string
  onChange: (next: string) => void
  /** Opens the "Generate from documents" sub-view. Omitted → the trigger is not rendered. */
  onGenerate?: () => void
  /** True while a derivation is in flight: skeleton textarea + the Analyzing row. */
  isDeriving?: boolean
  /** Distinct staged filenames, for the Analyzing copy. */
  derivingFilenames?: string[]
  /** Aborts an in-flight derivation. Supplied only where the host has no other
   *  dismiss control (the Settings pane) → renders a Cancel beside "Analyzing…". */
  onCancelDerive?: () => void
  /**
   * Read-only mode (AI-64 CASL gate): the host has `access` but not `manage` on
   * RecoveryAI. The textarea and the Generate trigger both go disabled; the
   * voice itself still renders. Defaults false so the wizard and the
   * Agent-Tuning modal call sites are unchanged.
   */
  disabled?: boolean
}

// Chrome-free, controlled brand-voice body (W7 FR-6). One editable textarea
// (prefilled by the host with the Thanx default) plus a THREE-way row beneath:
//   (1) idle + still the default → Generate + "You're using Thanx default voice"
//   (2) idle + edited            → Generate, no hint
//   (3) analyzing                → spinner + "Analyzing …", no Generate, no hint
// The Generate trigger is gated only on onGenerate being supplied; the
// textarea/edit path is ALWAYS available. No fetch, no save, no page chrome.
const BrandVoiceBody = ({
  value,
  onChange,
  onGenerate,
  isDeriving = false,
  derivingFilenames = [],
  onCancelDerive,
  disabled = false,
}: Props) => {
  const [css] = useStyletron()

  const showGenerate = !isDeriving && !!onGenerate
  const showHint = !isDeriving && isDefaultBrandVoiceText(value)

  const rowJustify = (): string => {
    if (isDeriving) return onCancelDerive ? 'space-between' : 'flex-start'
    if (showGenerate) return 'space-between'
    return 'flex-end'
  }

  return (
    <div>
      <BrandVoiceTextarea
        value={value}
        onChange={onChange}
        isDeriving={isDeriving}
        disabled={disabled}
      />
      <div
        className={`d-flex align-items-center ${css({
          marginTop: ROW_MARGIN_TOP,
          justifyContent: rowJustify(),
        })}`}
      >
        {isDeriving ? (
          <>
            {/* No aria-label: it would become the region's accessible name and
                suppress the visible "Analyzing <files>" content for AT. Let the
                polite live region announce that text (the specific filenames). */}
            <div
              role="status"
              aria-live="polite"
              className="d-flex align-items-center"
            >
              <Spinner isLoading size="1x" />
              <Text.BodyText4 color="grey70" className="ml-xs">
                <em>
                  {t('analyzing', {
                    files: joinBrandVoiceFilenames(derivingFilenames),
                  })}
                </em>
              </Text.BodyText4>
            </div>
            {/* Abort control, only where the host has no other escape (the pane).
                The Agent-Tuning modal relies on its footer Cancel instead. */}
            {onCancelDerive && (
              <Button kind="minimal" onClick={onCancelDerive}>
                {t('cancel_generate')}
              </Button>
            )}
          </>
        ) : (
          <>
            {showGenerate && (
              <Button kind="secondary" onClick={onGenerate} disabled={disabled}>
                <i className="fa fa-file-text-o mr-xs" aria-hidden="true" />
                {t('generate_from_documents')}
              </Button>
            )}
            {showHint && (
              <Text.BodyText4 color="grey70">
                {t('using_default_hint')}
              </Text.BodyText4>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default BrandVoiceBody
